import { useEffect, useState } from "react";

// Ported from the reference message-bubble.tsx link-preview block, with the
// --wa-* palette swapped for app tokens: OG metadata fetched through free
// CORS proxies, cached in localStorage with a TTL, and a per-host icon probe
// chain (apple-touch-icon → precomposed → Google favicon service).

const URL_REGEX = /https?:\/\/[^\s<>"]+/g;

export function firstUrl(text: string): string | null {
  URL_REGEX.lastIndex = 0;
  const match = URL_REGEX.exec(text);
  return match ? match[0] : null;
}

interface LinkMeta {
  title?: string;
  description?: string;
  image?: string;
}

// Persisted, TTL'd cache so previews survive reloads: we don't re-hit the
// (rate-limited, flaky) proxy for metadata, nor re-probe apple-touch-icon /
// favicon URLs on every mount. In-memory Map mirrors localStorage for sync
// reads.
const META_CACHE_PREFIX = "mandoob:linkmeta:";
const ICON_CACHE_PREFIX = "mandoob:linkicon:";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function readCache<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const entry = JSON.parse(raw) as { t: number; v: T };
    if (Date.now() - entry.t > CACHE_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return entry.v;
  } catch {
    return null;
  }
}

function writeCache<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify({ t: Date.now(), v: value }));
  } catch {
    // localStorage unavailable or over quota — caching is best-effort.
  }
}

const linkMetaCache = new Map<string, LinkMeta>();

function getCachedMeta(url: string): LinkMeta | null {
  const cached = linkMetaCache.get(url);
  if (cached) return cached;
  const stored = readCache<LinkMeta>(META_CACHE_PREFIX + url);
  if (stored) linkMetaCache.set(url, stored);
  return stored;
}

function setCachedMeta(url: string, meta: LinkMeta): void {
  linkMetaCache.set(url, meta);
  writeCache(META_CACHE_PREFIX + url, meta);
}

// Remembers, per host, which icon URL actually loaded, so we skip the
// apple-touch-icon / favicon probe chain on subsequent renders.
const iconCache = new Map<string, string>();

function getCachedIcon(host: string): string | null {
  const cached = iconCache.get(host);
  if (cached) return cached;
  const stored = readCache<string>(ICON_CACHE_PREFIX + host);
  if (stored) iconCache.set(host, stored);
  return stored;
}

function setCachedIcon(host: string, iconUrl: string): void {
  if (iconCache.get(host) === iconUrl) return;
  iconCache.set(host, iconUrl);
  writeCache(ICON_CACHE_PREFIX + host, iconUrl);
}

// Free, keyless CORS proxies, tried in order until one returns usable HTML.
// They're all volunteer services with no SLA, so we fall through on failure.
// corsproxy.io is first: it gates on the browser Origin header, so it works
// in-app even though it can't be exercised from a plain server request.
const CORS_PROXIES: ((url: string) => string)[] = [
  (url) => `https://corsproxy.io/?url=${encodeURIComponent(url)}`,
  (url) => `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
  (url) =>
    `https://api.codetabs.com/v1/proxy/?quest=${encodeURIComponent(url)}`,
];

function parseLinkMeta(html: string, url: string): LinkMeta {
  const doc = new DOMParser().parseFromString(html, "text/html");

  const pick = (selectors: string[]): string | undefined => {
    for (const selector of selectors) {
      const el = doc.querySelector(selector);
      const value = (el?.getAttribute("content") ?? el?.textContent)?.trim();
      if (value) return value;
    }
    return undefined;
  };

  let image = pick([
    'meta[property="og:image"]',
    'meta[name="twitter:image"]',
    'meta[name="twitter:image:src"]',
  ]);
  if (image) {
    try {
      image = new URL(image, url).href;
    } catch {
      image = undefined;
    }
  }

  return {
    title: pick([
      'meta[property="og:title"]',
      'meta[name="twitter:title"]',
      "title",
    ]),
    description: pick([
      'meta[property="og:description"]',
      'meta[name="twitter:description"]',
      'meta[name="description"]',
    ]),
    image,
  };
}

async function fetchLinkMeta(url: string): Promise<LinkMeta> {
  for (const buildProxyUrl of CORS_PROXIES) {
    try {
      const res = await fetch(buildProxyUrl(url));
      if (!res.ok) continue;
      const html = await res.text();
      const meta = parseLinkMeta(html, url);
      if (meta.title || meta.description || meta.image) return meta;
    } catch {
      // Proxy unavailable — fall through to the next one.
    }
  }
  return {};
}

export function LinkPreview({ url }: { url: string }) {
  const [iconIndex, setIconIndex] = useState(0);
  const [ogFailed, setOgFailed] = useState(false);
  const [meta, setMeta] = useState<LinkMeta | null>(() => getCachedMeta(url));

  let host: string | null;
  try {
    host = new URL(url).hostname;
  } catch {
    host = null;
  }

  useEffect(() => {
    const cached = getCachedMeta(url);
    if (cached) {
      setMeta(cached);
      return;
    }
    let cancelled = false;
    fetchLinkMeta(url)
      .then((result) => {
        setCachedMeta(url, result);
        if (!cancelled) setMeta(result);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!host) return null;

  const title = meta?.title || host.replace(/^www\./, "");
  const rawDescription = meta?.description || url;
  const description =
    rawDescription.length > 128 ? rawDescription.slice(0, 128) : rawDescription;

  // Prefer the real OG image; otherwise use the previously-resolved icon for
  // this host, or walk the keyless probe chain once and cache what loads.
  const cachedIcon = getCachedIcon(host);
  const iconSources = cachedIcon
    ? [cachedIcon]
    : [
        `https://${host}/apple-touch-icon.png`,
        `https://${host}/apple-touch-icon-precomposed.png`,
        `https://www.google.com/s2/favicons?domain=${host}&sz=128`,
      ];
  const ogImage = meta?.image && !ogFailed ? meta.image : null;
  const showFallback = !ogImage && iconIndex >= iconSources.length;

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      aria-label={title}
      className="tw:mb-1.5 tw:flex tw:w-full tw:min-w-56 tw:max-w-full tw:items-stretch tw:gap-2.5 tw:overflow-hidden tw:rounded-lg tw:bg-black/[0.05] tw:no-underline tw:dark:bg-white/[0.06]"
    >
      <div className="tw:flex tw:size-16 tw:shrink-0 tw:items-center tw:justify-center tw:bg-brand-50">
        {ogImage ? (
          <img
            src={ogImage}
            alt=""
            loading="lazy"
            onError={() => {
              // Stale/broken OG image — forget it so we don't retry it.
              setCachedMeta(url, { ...meta, image: undefined });
              setOgFailed(true);
            }}
            className="tw:size-full tw:object-cover"
          />
        ) : showFallback ? (
          <span className="tw:text-2xl tw:font-bold tw:text-brand">?</span>
        ) : (
          <img
            key={iconIndex}
            src={iconSources[iconIndex]}
            alt=""
            loading="lazy"
            onLoad={() => setCachedIcon(host, iconSources[iconIndex])}
            onError={() => setIconIndex((index) => index + 1)}
            className="tw:size-full tw:object-cover"
          />
        )}
      </div>
      <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:justify-center tw:py-1.5 tw:pe-2.5">
        <span className="tw:truncate tw:text-sm tw:font-semibold tw:text-foreground">
          {title}
        </span>
        <span className="tw:line-clamp-2 tw:break-all tw:text-xs tw:text-muted-foreground">
          {description}
        </span>
      </div>
    </a>
  );
}
